import {
	userProfile,
	auth,
	pharmacyTimeOffLists,
	chatUserLists,
	pharmacyWallPostsList,
	userCommentReplyList,
	userWallList,
	pharmacyTeamNotesMessage,
	recentChatLists,
	authPharmacyPermissions,
	pharmacyChatList
} from '../../constants/rootReduceName';

import { useSelector } from 'react-redux';
import { useAdminStealthHidden } from '../../hooks/useAdminStealth';
import { useCommonReducerSelector } from './commonReducerSelector';
import { normalizeAssetUrl } from '../../utils/helper/assetUrl';

export const useCustomSelectors = () => {

	const authData = useSelector(state => state[auth])
	const pharmacyList = (authData?.data?.pharmacy || []).map((pharmacy) => ({
		...pharmacy,
		logo: normalizeAssetUrl(pharmacy?.logo),
		logo_full_url: normalizeAssetUrl(pharmacy?.logo_full_url),
		logo_thumb_full_url: normalizeAssetUrl(pharmacy?.logo_thumb_full_url),
	}));
	const pharmacyId = pharmacyList?.[0]?.id

	const automationEnabled = authData?.data?.settings?.automation_enabled ?? false

	// const pharmacyLoginUser = authData?.data?.pharmacyloginuser?.[0]?.role
	const pharmacyLoginData = pharmacyList?.[0]
	const pharmacyRoutePasscode = authData?.data?.pharmacy?.[0]?.route_passcode
	const emrStatus = authData?.data?.pharmacy?.[0]?.emr_status
	// Blister visibility, set per pharmacy in admin Permission Modules.
	// Absent means visible, so these default to true.
	const blisterPatientsEnabled = authData?.data?.pharmacy?.[0]?.blister_patients_enabled !== false
	const blisterPackTabEnabled = authData?.data?.pharmacy?.[0]?.blister_pack_tab_enabled !== false
	const oat_sheet_enable = authData?.data?.pharmacy?.[0]?.oat_sheet_enable === true 
	const emergencySignaturePool = authData?.data?.pharmacy?.[0]?.emergency_signature_pool === true
		|| authData?.data?.pharmacy?.[0]?.emergency_signature_pool === 1
		|| authData?.data?.pharmacy?.[0]?.emergency_signature_pool === '1'
		|| authData?.data?.pharmacy?.[0]?.emergency_signature_pool === 'true'

	// ADMIN SCREEN PRIVACY (F9 x5, see useAdminStealth). Every admin-only
	// item in the portal is already gated on `adminModule === 1`, so hiding
	// them is expressed here, once, as an EFFECTIVE adminModule of 0 — the
	// same value a normal team member has. No consumer needs to change, and
	// the hidden portal is produced by the code path that already serves
	// normal users every day.
	//
	// PRESENTATION ONLY. It changes what is drawn, never what the user is
	// authorised to do: the backend is untouched and still decides that.
	// `adminStealthHidden` is false for every non-admin and false whenever
	// the backend cannot answer, so this line is a no-op for everyone else.
	//
	// THE RULE THIS MUST OBEY: hiding may only ever REMOVE something from the
	// screen, never add one. Nearly every check in the portal reads
	// `adminModule === 1` as "show this to admins", and those hide correctly
	// when the effective value drops to 0. A HANDFUL are inverted — they show
	// something BECAUSE the user is not an admin (the Time Off Request, Tasks
	// Assignments and Appreciations header icons, which admins reach through
	// TEAM instead). Reading the effective value there would make hiding ADD
	// three icons, so those few read `rawAdminModule` below: the user's real
	// flag, which screen privacy never touches.
	const adminStealthHidden = useAdminStealthHidden()
	// The user's TRUE admin flag. Only for conditions that show something to
	// NON-admins; everything else must use `adminModule`.
	const rawAdminModule = authData?.data?.pharmacyloginuser?.[0]?.admin_module
	const adminModule = adminStealthHidden ? 0 : rawAdminModule
	const loginType = authData?.data?.pharmacyloginuser?.[0]?.type
	const assignchat = authData?.data?.pharmacyloginuser?.[0]?.assign_chat?.pharmacist_id
	const teamStatus = authData?.data?.pharmacyloginuser?.[0]?.team_status
	// This is a pharmacy setting, not a user setting. The login/2FA response
	// returns it on `pharmacy`; retain the former user field as a fallback for
	// older API responses.
	const pendingMarkDeliveryAccess = authData?.data?.pharmacy?.[0]?.pending_mark_delivery_access
		?? authData?.data?.pharmacyloginuser?.[0]?.pending_mark_delivery_access
	// Pharmacy-level opt-in for Team users. It defaults to disabled when the
	// backend has not returned the setting yet, so existing access is unchanged.
	const teamMarkDeliveryAccess = authData?.data?.pharmacy?.[0]?.team_mark_delivery_access

	const enableLogSheet = authData?.data?.enable_log_sheet ?? true;
	
	const pharmacyTabsPermssion = useSelector(state => state[authPharmacyPermissions])
	const pharmacyLoginUser = pharmacyTabsPermssion?.data?.permissions

	const userProfileResponse = useSelector(state => state[userProfile])
	const profileData = userProfileResponse?.profileData?.data
		? {
			...userProfileResponse.profileData.data,
			photo: normalizeAssetUrl(userProfileResponse.profileData.data.photo),
			thumbnail: normalizeAssetUrl(userProfileResponse.profileData.data.thumbnail),
			cover_photo: normalizeAssetUrl(userProfileResponse.profileData.data.cover_photo),
		}
		: userProfileResponse?.profileData?.data

	const loginUserID = profileData?.user_id

	const fileManagerUrl = userProfileResponse?.filemangerurl
	const accountabilitySettings = userProfileResponse?.accountabilitySettings
	const showAfterProcessPrepCheck = userProfileResponse?.showAfterProcessPrepCheck ?? false

	const getTotalHours = authData.data?.pharmacy?.[0]?.total_hours
	const pharmacyWallPostList = useSelector(state => state[pharmacyWallPostsList]);
	const wallPostLists = pharmacyWallPostList?.pharmacyWallPosts?.data?.data;
	const totalPagination = pharmacyWallPostList?.pharmacyWallPosts?.data?.pagination?.last_page;

	const commentList = useSelector(state => state?.allPostCommentLists);
	const allCommentList = commentList?.commentList?.data;

	const addCommentResponse = useSelector(state => state?.wallPostUserComment);
	const commentReplyResponse = useSelector(state => state?.userCommentReply);

	const commentReplyList = useSelector(state => state[userCommentReplyList]?.commentReplyList?.data);
	const addWallPostResponse = useSelector(state => state.addWallPosts);

	const allEmployeeData = useSelector(state => state?.allEmployeeData)
	const employeeData = allEmployeeData?.data?.data

	const addChatMessageResponse = useSelector(state => state.addChatMessages)

	const chatList = useSelector(state => state.chatMessageLists)
	const opponentChatMessages = useSelector(state => state.getChatMessages)
	const addMessagesToGroupResponse = useSelector(state => state.addChatMessages)


	const pharmacyIntiatedChatResponse = useSelector(state => state?.initiatedChatGroup)
	const initatedChatMessagesResponse = pharmacyIntiatedChatResponse?.data?.data?.messages

	const employeeAppreciationsResponse = useSelector(state => state.employeeAppreciations)

	const chatUserList = useSelector(state => state[chatUserLists])
	const userActivitiyStatus = chatUserList?.data?.data?.find((value) => value?.user_id === profileData?.user_id)

	const updateProfileResponse = useSelector(state => state.updateProfile)
	const timeOffRequestResponse = useSelector(state => state.timeOffReqest)

	const pharmacyTimeOffList = useSelector(state => state[pharmacyTimeOffLists])

	const recentChatList = useSelector(state => state[recentChatLists])
	const chatUsers = useSelector(state => state[pharmacyChatList])

	const userWallListResponse = useSelector(state => state[userWallList])
	const userWallLists = userWallListResponse?.data?.data?.data

	const addClinicResponse = useSelector(state => state.addClinic)

	const teamNotesMessageResponse = useSelector(state => state[pharmacyTeamNotesMessage])

	const patientListNotificationsResponse = useSelector(state => state.pharmacyPatientNotifications)
	const patientNotificationsList = patientListNotificationsResponse?.data?.data

	const pharmacyPatientAddNoteReply = useSelector(state => state.pharmacyPatientAddNoteReply)
	const patientNoteMarkAsRead = useSelector(state => state.pharmacyPatientNoteMarkAsRead)

	const pharmacyAllTableListResponse = useSelector(state => state.pharmacyAllTableList)
	const pharmacyAddDataResponse = useSelector(state => state.pharmacyAddData)
	const patientSoapNotesResponse = useSelector((state) => state.patientSoapNotes)

	const { bookkeeperModuleAccessResponse } = useCommonReducerSelector()
	const bookkeeperModuleAccess = bookkeeperModuleAccessResponse

	return {
		oat_sheet_enable,
		pharmacyWallPostList,
		wallPostLists,
		totalPagination,
		commentList,
		allCommentList,
		addCommentResponse,
		commentReplyResponse,
		commentReplyList,
		addWallPostResponse,
		profileData, allEmployeeData,
		employeeData,
		chatUserList,
		addChatMessageResponse,
		chatList,
		pharmacyIntiatedChatResponse,
		initatedChatMessagesResponse,
		opponentChatMessages,
		addMessagesToGroupResponse,
		authData,
		pharmacyId,
		employeeAppreciationsResponse,
		userActivitiyStatus,
		userProfileResponse,
		updateProfileResponse,
		getTotalHours,
		timeOffRequestResponse,
		pharmacyTimeOffList,
		recentChatList,
		userWallListResponse,
		userWallLists,
		pharmacyLoginUser,
		pharmacyLoginData,
		addClinicResponse,
		teamNotesMessageResponse,
		patientListNotificationsResponse,
		patientNotificationsList,
		pharmacyPatientAddNoteReply,
		patientNoteMarkAsRead,
		pharmacyAllTableListResponse,
		pharmacyAddDataResponse,
		adminModule,
		rawAdminModule,
		chatUsers,
		assignchat,
		loginType,
		patientSoapNotesResponse,
		pharmacyRoutePasscode,
		emrStatus,
		blisterPatientsEnabled,
		blisterPackTabEnabled,
		fileManagerUrl,
		accountabilitySettings,
		showAfterProcessPrepCheck,
		loginUserID,
		teamStatus,
		pendingMarkDeliveryAccess,
		teamMarkDeliveryAccess,
		bookkeeperModuleAccess,
		enableLogSheet,
		automationEnabled,
		emergencySignaturePool
	};
};

